import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { UserInfo } from '../../../models/user-info.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { Router } from '@angular/router';
import { UsersService } from 'src/app/services/users/users.service';
import { entAnimations } from 'src/app/animations';
import {
  debounceTime,
  distinctUntilChanged,
  lastValueFrom,
  Subscription,
} from 'rxjs';
import { Error } from 'src/app/models/error.model';
import { MatTableDataSource } from '@angular/material/table';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { ExchangeListFields } from 'src/app/models/resource-list-fields.model';
import { TeamsClassAddDialogComponent } from './teams-class-add-dialog/teams-class-add-dialog.component';
import { TeamsStandardAddDialogComponent } from './teams-standard-add-dialog/teams-standard-add-dialog.component';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-teams',
  templateUrl: './teams.component.html',
  styleUrl: './teams.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class TeamsComponent implements OnInit, OnDestroy {
  TeamsClassAddDialogComponent = TeamsClassAddDialogComponent;
  TeamsStandardAddDialogComponent = TeamsStandardAddDialogComponent;

  private subscription: Subscription = new Subscription();

  isLoading = false;
  selectedUser: UserInfo = new UserInfo();
  currentUser: UserInfo;
  hasNoClassTeam = false;
  hasNoStandardTeam = false;
  errorGetTeams: Error = { error: { message: '', code: '' } };
  isErrorGetTeams = false;
  teamsList: ExchangeListFields[];
  selectedEtablissement: string;
  standardTeamsDataSource: MatTableDataSource<ExchangeListFields>;
  classTeamsDataSource: MatTableDataSource<ExchangeListFields>;
  defaultColumns: string[] = ['state', 'displayName', 'email', 'view'];
  displayedColumns: string[] = [];
  hiddenColumns: string[] = [];
  defaultClassColumns: string[] = ['state', 'displayName', 'email', 'view'];
  hiddenClassColumns: string[] = [];
  displayedClassColumns: string[] = [];
  schoolYears: string[] = [];
  selectedSchoolYear = '';
  nameClassFilter = new FormControl('');
  nameStandardFilter = new FormControl('');
  emailClassFilter = new FormControl('');
  emailStandardFilter = new FormControl('');
  windowWidth: number = window.innerWidth;
  constructor(
    private _router: Router,
    private _settingsService: SettingsService,
    private _graphService: GraphService,
    private _userService: UsersService,
    private _localStorageService: LocalStorageService,
    private _toolboxService: ToolBoxService
  ) {
    this.currentUser = this._userService.currentUserValue;
  }

  initSchoolYears() {
    this.selectedSchoolYear = this._toolboxService.getCurrentSchoolYear();
    let currentSchoolYear = +this.selectedSchoolYear.split(' / ')[0];
    if (this._toolboxService.canShowNextYear()) {
      currentSchoolYear += 1;
    }
    for (let i = currentSchoolYear; i > currentSchoolYear - 4; i--) {
      this.schoolYears.push(this._toolboxService.getCustomSchoolYear(i));
    }
  }

  async ngOnInit(): Promise<void> {
    const geSubscription = this._settingsService.currentGE$.subscribe(ge => {
      this.selectedEtablissement = ge;
      this.onEtablissementChange();
    });
    this.subscription.add(geSubscription);
    this.selectedSchoolYear = this._toolboxService.getCurrentSchoolYear();
    this.selectedEtablissement = await this._toolboxService.getUserEtablissement();
    this.updateTeamsList();
    this.initSchoolYears();
    this.nameClassFilter.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.updateClassTeams();
      });
    this.nameStandardFilter.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.updateStandardTeams();
      });
    this.emailStandardFilter.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.standardTeamsDataSource.filter = value || '';
      });
    this.emailClassFilter.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.classTeamsDataSource.filter = value || '';
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.windowWidth = window.innerWidth;
    this.updateDisplayedColumns();
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = [...this.defaultColumns];
    this.displayedClassColumns = [...this.defaultClassColumns];
    this.hiddenColumns = [];
    this.hiddenClassColumns = [];

    if (this.windowWidth < 950) {
      this._toolboxService.hideTableColumn(
        'view',
        this.displayedColumns,
        this.hiddenColumns
      );
    }

    if (this.windowWidth < 750) {
      this._toolboxService.hideTableColumn(
        'email',
        this.displayedColumns,
        this.hiddenColumns
      );

      this._toolboxService.hideTableColumn(
        'email',
        this.displayedClassColumns,
        this.hiddenClassColumns
      );
    }

    if (this.windowWidth < 500) {
      this._toolboxService.hideTableColumn(
        'state',
        this.displayedColumns,
        this.hiddenColumns
      );

      this._toolboxService.hideTableColumn(
        'state',
        this.displayedClassColumns,
        this.hiddenClassColumns
      );
    }
  }

  private onEtablissementChange() {
    this._localStorageService.removeItem('myUsers'); // reset values when changing etab
    this._localStorageService.removeItem('UsersTeam');
    this._localStorageService.removeItem('contextStorageUsersMailbox');
    this.updateTeamsList();
  }

  updateTeamsList() {
    this.updateStandardTeams();
    this.updateClassTeams();
    this.updateDisplayedColumns();
  }

  async updateStandardTeams() {
    const result = await this.getListOfTeam(
      'standard',
      null,
      this.nameStandardFilterValue
    );
    this.standardTeamsDataSource = result.dataSource;
    this.hasNoStandardTeam = result.hasNoTeams;
    this.standardTeamsDataSource.filterPredicate = (
      data: any,
      filter: string
    ) => {
      return data.email?.toLowerCase().includes(filter.trim().toLowerCase());
    };
  }

  async updateClassTeams() {
    const result = await this.getListOfTeam(
      'class',
      this._toolboxService.formatYear(this.selectedSchoolYear),
      this.nameClassFilterValue
    );
    this.classTeamsDataSource = result.dataSource;
    this.hasNoClassTeam = result.hasNoTeams;
    this.defaultSchoolYearSorting();
    this.classTeamsDataSource.filterPredicate = (data: any, filter: string) => {
      return data.email?.toLowerCase().includes(filter.trim().toLowerCase());
    };
  }

  defaultSchoolYearSorting() {
    this.classTeamsDataSource.data = this.classTeamsDataSource.data.sort(
      (a, b) => {
        if (a.schoolYear === null || b.schoolYear === null) {
          return -1;
        }
        return +a.schoolYear.split('-')[0] > +b.schoolYear.split('-')[0]
          ? -1
          : 1;
      }
    );
  }

  onSchoolYearChange(schoolYear: string) {
    this.selectedSchoolYear = schoolYear;
    this.updateClassTeams();
  }

  async getListOfTeam(
    type: string,
    schoolYear: string = null,
    nameFilter = ''
  ): Promise<{
    dataSource: MatTableDataSource<ExchangeListFields>;
    hasNoTeams: boolean;
  }> {
    let dataSource: MatTableDataSource<ExchangeListFields>;
    let hasNoTeams = false;

    try {
      if (this.selectedEtablissement) {
        this.isLoading = true;
        this.errorGetTeams = { error: { message: '', code: '' } };
        this.isErrorGetTeams = false;

        this.teamsList = await lastValueFrom(
          this._graphService.getListTeams(
            this.selectedEtablissement,
            type,
            schoolYear,
            nameFilter
          )
        );

        if (this.teamsList.length === 0) {
          hasNoTeams = true;
        }

        this.isLoading = false;

        dataSource = new MatTableDataSource(this.teamsList);
        return { dataSource, hasNoTeams };
      }

      return null;
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText === 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (customError.error !== undefined) {
          if (customError.error.code === 'ERR403') {
            this._router.navigate(['/errors/error-403']);
          } else {
            this._router.navigate(['/errors/error-404']);
          }
        }
      }

      this.isLoading = false;
      this.errorGetTeams = customError;
      this.isErrorGetTeams = true;

      return null;
    }
  }

  get nameClassFilterValue(): string {
    return this.nameClassFilter.value;
  }

  set nameClassFilterValue(value: string) {
    this.nameClassFilter.setValue(value);
  }

  get nameStandardFilterValue(): string {
    return this.nameStandardFilter.value;
  }

  set nameStandardFilterValue(value: string) {
    this.nameStandardFilter.setValue(value);
  }
}
